import { useState } from 'react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { toast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { usePermissions } from '../../../../foundation/altzero_base/base/contextapi/PermissionsContext';
import { billEmailService } from '../../services/billEmailService';
import { defaultGapRange, gapSpan, isGapDay, type GapReport } from '../../lib/gapCheck';

type InboxGapCheckProps = {
  /** Starts a sync of the given days (the Inbox page's Sync now, so its progress card shows). */
  onSyncRange: (range: { from: string; to: string }) => Promise<void> | void;
};

type RescanConfirm = { from: string; oldWatermark: string | null };

const fmtWatermark = (iso: string | null): string =>
  iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : 'none';

/**
 * Backfill controls (Task D3): a read-only per-day Gmail vs stored check, a sync
 * of the highlighted days, and (admins) moving the automatic sync back to a date.
 * All are safe to repeat: stored emails are skipped before any download.
 */
export const InboxGapCheck = ({ onSyncRange }: InboxGapCheckProps) => {
  const { isAdmin } = usePermissions();
  const initial = defaultGapRange(new Date());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [report, setReport] = useState<GapReport | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const [rescanDate, setRescanDate] = useState(initial.from);
  const [confirm, setConfirm] = useState<RescanConfirm | null>(null);
  const [rescanBusy, setRescanBusy] = useState(false);
  const [rescanError, setRescanError] = useState<string | null>(null);

  const span = report ? gapSpan(report.rows) : null;

  const handleCheck = async () => {
    setChecking(true);
    setError(null);
    try {
      const res = await billEmailService.checkGaps(from, to);
      if (res.success && res.data) setReport(res.data);
      else {
        setReport(null);
        setError(res.error || 'Gap check failed');
      }
    } catch (err) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setChecking(false);
    }
  };

  const handleSyncGaps = async () => {
    if (!span) return;
    setSyncing(true);
    try {
      await onSyncRange(span);
    } finally {
      setSyncing(false);
    }
  };

  // Step 1: read the current watermark so the confirmation can show old → new.
  const handleRescanStart = async () => {
    setRescanBusy(true);
    setRescanError(null);
    try {
      const res = await billEmailService.getConfig();
      if (!res.success) {
        setRescanError(res.error || 'Could not read the sync watermark');
        return;
      }
      setConfirm({ from: rescanDate, oldWatermark: res.data?.last_polled_at ?? null });
    } catch (err) {
      setRescanError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRescanBusy(false);
    }
  };

  // Step 2: confirmed in the page.
  const handleRescanConfirm = async () => {
    if (!confirm) return;
    setRescanBusy(true);
    setRescanError(null);
    try {
      const res = await billEmailService.rescanFrom(confirm.from);
      if (!res.success || !res.data) {
        setRescanError(res.error || 'Re-scan failed');
        return;
      }
      setConfirm(null);
      toast({
        title: 'Re-scan scheduled',
        description: `The automatic sync now reads from ${fmtWatermark(res.data.newWatermark)} (was ${fmtWatermark(res.data.oldWatermark)}).`,
      });
    } catch (err) {
      setRescanError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRescanBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t pt-3" data-testid="inbox-gap-check">
      <div className="flex flex-wrap items-end gap-2">
        <div className="text-sm font-medium text-gray-900 mr-2 self-center">Check for gaps</div>
        <label className="text-xs text-gray-500">
          From
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40" />
        </label>
        <label className="text-xs text-gray-500">
          To
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40" />
        </label>
        <Button size="sm" variant="outline" onClick={handleCheck} disabled={checking || !from || !to}>
          {checking ? 'Checking Gmail…' : 'Check for gaps'}
        </Button>
        <Button
          size="sm"
          onClick={handleSyncGaps}
          disabled={!span || syncing}
          title={span ? `Sync ${span.from} to ${span.to}` : 'No gap days to sync'}
        >
          {syncing ? 'Starting sync…' : 'Sync gap days'}
        </Button>
      </div>
      <p className="mt-1 text-xs text-gray-400">
        Read-only, up to 31 days. Compares what Gmail holds per Gmail day (Pacific time, the sync&apos;s own query) with what is stored; a day is
        highlighted when more than 5 and more than 10% are missing. Some difference is normal: emails with no PDF or Word
        file are not stored.
      </p>

      {checking && <div className="mt-3 h-16 animate-pulse rounded bg-gray-100" />}
      {!checking && error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {!checking && !error && report && report.rows.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">No days in that range.</p>
      )}
      {!checking && !error && report && report.rows.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium" title="Gmail day, Pacific time — the same day the sync queries">Gmail day</th>
                <th className="px-2 py-2 text-right font-medium">In Gmail</th>
                <th className="px-2 py-2 text-right font-medium">Stored</th>
                <th className="px-2 py-2 text-right font-medium">Missing</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.day} className={cn('border-b last:border-0', isGapDay(r) && 'bg-amber-50')}>
                  <td className="py-1.5 pr-3 tabular-nums text-gray-700">{r.day}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{r.inGmail}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{r.stored}</td>
                  <td className={cn('px-2 py-1.5 text-right tabular-nums', isGapDay(r) ? 'font-medium text-amber-700' : 'text-gray-700')}>
                    {r.missing}
                  </td>
                </tr>
              ))}
              <tr className="text-xs text-gray-500">
                <td className="py-1.5 pr-3">Total</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{report.totals.inGmail}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{report.totals.stored}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{report.totals.missing}</td>
              </tr>
            </tbody>
          </table>
          {!span && <p className="mt-2 text-xs text-gray-500">No gap days in this range.</p>}
        </div>
      )}

      {isAdmin && (
        <div className="mt-4 border-t pt-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="text-sm font-medium text-gray-900 mr-2 self-center">Re-scan from date</div>
            <Input
              type="date"
              value={rescanDate}
              onChange={(e) => { setRescanDate(e.target.value); setConfirm(null); }}
              className="h-8 w-40"
              aria-label="Re-scan from date"
            />
            {!confirm && (
              <Button size="sm" variant="outline" onClick={handleRescanStart} disabled={rescanBusy || !rescanDate}>
                {rescanBusy ? 'Reading…' : 'Re-scan…'}
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Admins only. Moves the automatic sync back to this date (at most 90 days, never forward); the next automatic
            run walks forward from there. Stored emails are skipped and assignments are not touched.
          </p>
          {confirm && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm" role="alert">
              <p className="text-gray-800">
                Move the sync watermark from <strong>{fmtWatermark(confirm.oldWatermark)}</strong> to{' '}
                <strong>{fmtWatermark(`${confirm.from}T00:00:00.000Z`)}</strong>?
              </p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={handleRescanConfirm} disabled={rescanBusy}>
                  {rescanBusy ? 'Moving…' : 'Confirm re-scan'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirm(null)} disabled={rescanBusy}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {rescanError && <p className="mt-2 text-sm text-red-600">{rescanError}</p>}
        </div>
      )}
    </div>
  );
};
